import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import { PsHttpService, type PatSnapSuccessResponseData } from '@patsnap/tahiti-core';
import { coreTelecomApiUrlPrefix } from '@telecom/shared/domain-config';
import type {
    NoveltySearchPrApi,
    SearchFeatureExtractRequest,
    SearchFeatureExtractResponse,
    SearchFeatureExtractResultRequest,
    SearchFeatureExtractResultResponse,
    CcCommonEvidenceRequest,
    CcCommonEvidenceResponse,
    PollCommonEvidenceResultBatchRequest,
    PollCommonEvidenceResultBatchResponse,
    GetFinalCommonEvidenceResultRequest,
    GetFinalCommonEvidenceResultResponse,
} from '../novelty-search-pr-api';

/**
 * 新颖性检索PR API实现
 * @description 实现新颖性检索相关的所有API操作，包括特征提取和证据对比
 */
@injectable()
export class NoveltySearchPrApiImpl extends PsHttpService implements NoveltySearchPrApi {
    get options() {
        return {
            baseURL: coreTelecomApiUrlPrefix,
            headers: {
                'X-API-Version': '1.0',
            },
        };
    }

    /**
     * 启动公知证据-特征提取&语义检索异步任务
     * @param data 搜索特征提取请求参数
     * @returns 搜索关键字列表
     * @description 根据任务ID启动特征拆解和语义检索异步任务
     */
    async searchFeatureExtract(data: SearchFeatureExtractRequest): Promise<SearchFeatureExtractResponse> {
        const response = await this.post<PatSnapSuccessResponseData<SearchFeatureExtractResponse>>({
            url: '/novelty-search/search-feature-extract',
            data,
        });
        return response.data;
    }
    /**
     * 获取公知证据-特征提取&语义检索异步任务状态
     * @param data 轮询特征拆解语义检索结果请求参数
     * @returns 任务状态
     * @description 轮询查询特征拆解和语义检索任务的执行状态
     */
    async searchFeatureExtractResult(
        data: SearchFeatureExtractResultRequest,
    ): Promise<SearchFeatureExtractResultResponse> {
        const response = await this.get<PatSnapSuccessResponseData<SearchFeatureExtractResultResponse>>({
            url: `/novelty-search/search-feature-extract/${data.task_id}`,
        });
        return response.data;
    }

    /**
     * 启动公知证据检索任务
     * @param data 常见证据对比请求参数
     * @returns patent_id 到 PatentEvidenceData 的映射
     * @description 对技术特征与现有专利进行对比分析，创建公知证据检索异步任务
     */
    async ccCommonEvidence(data: CcCommonEvidenceRequest): Promise<CcCommonEvidenceResponse> {
        const response = await this.post<PatSnapSuccessResponseData<CcCommonEvidenceResponse>>({
            url: '/novelty-search/cc-common-evidence',
            data,
        });
        return response.data;
    }

    /**
     * 批量查询公知证据检索任务状态
     * @param data 批量轮询请求参数
     * @returns patent_id 到 PollCommonEvidenceResultResponse 的映射
     * @description 批量查询多个专利的公知证据对比结果
     */
    async getCommonEvidenceResultBatch(
        data: PollCommonEvidenceResultBatchRequest,
    ): Promise<PollCommonEvidenceResultBatchResponse> {
        const response = await this.post<
        PatSnapSuccessResponseData<PollCommonEvidenceResultBatchResponse>
        >({
            url: '/novelty-search/cc-common-evidence-result',
            data,
        });

        return response.data;
    }

    /**
     * 获取公知证据检索最终结果
     * @param data 获取最终结果请求参数
     * @returns patent_id 到 PollCommonEvidenceResultResponse 的映射
     * @description 直接从数据库中读取完整的证据映射数据，适用于已完成的任务
     */
    async getFinalCommonEvidenceResult(
        data: GetFinalCommonEvidenceResultRequest,
    ): Promise<GetFinalCommonEvidenceResultResponse> {
        const response = await this.get<PatSnapSuccessResponseData<GetFinalCommonEvidenceResultResponse>>({
            url: `/novelty-search/cc-common-evidence-final-result/${data.task_id}`,
        });

        return response.data;
    }
}
